import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/networks/networksApi'
import { searchParams, statusParams, type ObjectStatus } from '@/services/core/listSearch'
import { sortParams, type ListPage, type ListSort } from '@/services/core/paging'
import { mapValidationWarnings, type ValidationWarning } from '@/services/core/validationWarnings'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

export interface UserRole {
  role: {
    id: string
    name: string
    url?: string
    application?: string
  }
  team?: {
    id: string
    name?: string
    objectState?: string
    url?: string
    type?: string
  }
  /** Set when the role is held on every team in a team group. */
  teamGroup?: {
    id: string
    name?: string
    objectState?: string
    url?: string
  }
}

export interface UserNetworkRole {
  network: {
    id: string
    tagId?: string
    name: string
    objectState?: string
    url?: string
  }
  roles: UserRole[]
}

export interface NetworkUser {
  id: string
  username: string
  name: string
  surname?: string
  displayName: string
  objectState: string
  locale: string
  preferredCurrency?: string
  externalReference?: string
  /** Set when the signed-in user may see but not change this user. */
  readOnly?: boolean
  twoFactorAuth?: boolean
  timeZone: string
  loginEnabled: boolean
  login2faRequired: boolean
  twoFactorAuthType: string
  lastLogin?: string
  lastPasswordChange?: string
  passwordExpiry?: string
  passwordExpiresDays?: number
  createTime: string
  updateTime: string
  /** Problems the server found on the last save; only set on a full fetch. */
  validationWarnings?: ValidationWarning[]
  userRoles: {
    rolesByNetwork: UserNetworkRole[]
  }
  url: string
}

/** The fields of a user the admin page edits. */
export interface UserInput {
  /** Only taken on create — a username cannot change once saved. */
  username?: string
  name: string
  surname?: string
  displayName: string
  locale?: string
  preferredCurrency?: string | null
  externalReference?: string
  objectState?: string
}

/** A role held in the network: network-wide, or on a team or team group. */
export interface RoleRef {
  role: string
  team?: string
  teamGroup?: string
}

/** One sign-in, as the login stats record it. */
export interface LoginStat {
  dateTime: string
}

export interface UsersResponse {
  results: NetworkUser[]
  totalCount: number
}

export interface UsersQueryParams {
  app?: string
  includeHidden?: boolean
  includeActive?: boolean
  includeInactive?: boolean
  minimal?: boolean
  orderBy?: string
  pageSize?: number
  /** Free text matched against the user's name, username and surname. */
  search?: string
}

export const UsersApi = {
  getAll: async (params?: UsersQueryParams): Promise<UsersResponse> => {
    const query = new URLSearchParams({
      ...searchParams('users', params?.search),
      context: getContext(),
      app: params?.app ?? 'ADVERTISING',
      includeHidden: String(params?.includeHidden ?? false),
      includeActive: String(params?.includeActive ?? true),
      includeInactive: String(params?.includeInactive ?? false),
      orderBy: params?.orderBy ?? 'username',
      pageSize: String(params?.pageSize ?? 2000),
    })
    if (params?.minimal) query.set('minimal', 'true')
    const res = await apiFetch(`${API_BASE}/users?${query}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) throw new Error(`Failed to fetch users (${res.status})`)
    const data: UsersResponse = await res.json()
    return data
  },

  /** One page of the network's users, narrowed by search and state on the server. */
  getPage: async ({
    page = 1,
    pageSize = 25,
    search,
    status = 'all',
    sort,
  }: {
    page?: number
    pageSize?: number
    search?: string
    status?: ObjectStatus
    sort?: ListSort | null
  }): Promise<ListPage<NetworkUser>> => {
    const query = new URLSearchParams({
      ...searchParams('users', search),
      context: getContext(),
      app: 'ADVERTISING',
      includeHidden: 'false',
      ...statusParams(status),
      ...sortParams(sort, { orderBy: 'username' }),
      page: String(page),
      pageSize: String(pageSize),
    })
    const res = await apiFetch(`${API_BASE}/users?${query}`, { headers: JSON_HEADERS })
    if (!res.ok) await throwApiError(res, 'Failed to fetch users')
    const data = (await res.json()) as { totalCount?: number }
    const results = toArray<NetworkUser>(data)
    return { results, totalCount: data.totalCount ?? results.length, page, pageSize }
  },

  getById: async (userId: string): Promise<NetworkUser> => {
    const res = await apiFetch(`${API_BASE}/users/${encodeURIComponent(userId)}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) throw new Error(`Failed to fetch user (${res.status})`)
    const raw = await res.json()
    return { ...raw, validationWarnings: mapValidationWarnings(raw.validationWarnings) }
  },

  update: async (userId: string, input: { objectState?: string; loginEnabled?: boolean }): Promise<NetworkUser> => {
    const res = await apiFetch(`${API_BASE}/users/${encodeURIComponent(userId)}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(input),
    })
    if (!res.ok) await throwApiError(res, 'Failed to update user')
    return res.json()
  },

  /**
   * Replaces the user's roles in one network. The API swaps out every role the
   * user holds there, so `roles` must include the ones being kept.
   */
  setNetworkRoles: async (
    userId: string,
    networkId: string,
    roles: Array<{ role: string; team?: string; teamGroup?: string }>
  ): Promise<NetworkUser> => {
    const res = await apiFetch(
      `${API_BASE}/users/${encodeURIComponent(userId)}?context=${getContext()}`,
      {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({
          userRoles: { rolesByNetwork: [{ network: networkId, roles }] },
        }),
      }
    )
    if (!res.ok) await throwApiError(res, 'Failed to update user roles')
    return res.json()
  },

  /**
   * Creates or updates a user with their roles in one network. The roles
   * replace every role the user holds in that network.
   */
  save: async (
    id: string,
    input: UserInput,
    networkId: string,
    roles: RoleRef[]
  ): Promise<NetworkUser> => {
    const res = await apiFetch(
      `${API_BASE}/users/${encodeURIComponent(id)}?context=${getContext()}`,
      {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({
          id,
          ...input,
          userRoles: { rolesByNetwork: [{ network: networkId, roles }] },
        }),
      }
    )
    if (!res.ok) await throwApiError(res, 'Failed to save user')
    const raw = await res.json()
    return { ...raw, validationWarnings: mapValidationWarnings(raw.validationWarnings) }
  },

  /** The user's recent sign-ins, newest first. */
  getLoginStats: async (userId: string): Promise<LoginStat[]> => {
    const res = await apiFetch(
      `${API_BASE}/loginstats/${encodeURIComponent(userId)}?format=json`,
      { headers: JSON_HEADERS }
    )
    if (!res.ok) await throwApiError(res, 'Failed to fetch sign-ins')
    const stats = toArray<LoginStat>(await res.json())
    return [...stats].sort((a, b) => b.dateTime.localeCompare(a.dateTime))
  },

  create: async (input: {
    name: string
    username: string
    teamId: string
    roleId: string
    memberRoleId: string
    networkId: string
  }): Promise<NetworkUser> => {
    const id = crypto.randomUUID().replace(/-/g, '').slice(0, 16)
    const payload = {
      id,
      name: input.name,
      displayName: input.name,
      username: input.username,
      userRoles: {
        rolesByNetwork: [
          {
            network: input.networkId,
            roles: [
              { team: input.teamId, role: input.roleId },
              { network: input.networkId, role: input.memberRoleId },
            ],
          },
        ],
      },
    }
    const res = await apiFetch(`${API_BASE}/users/${encodeURIComponent(id)}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(payload),
    })
    if (!res.ok) await throwApiError(res, 'Failed to create user')
    return res.json()
  },
}
